import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/currency";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plane, Ship, Download, Search, ChevronRight, Pencil, Building2, Plus, Check, Send } from "lucide-react";
import { CommDialog } from "@/components/AdminComm";

// ---------- helpers ----------
export function exportCsv(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [keys.join(";"), ...rows.map((r) => keys.map((k) => esc(r[k])).join(";"))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export const ListToolbar = ({ search, setSearch, count, onExport, testid }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2">
    <div className="relative flex-1 min-w-[200px] max-w-sm">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input data-testid={`${testid}-search`} className="rounded-sm pl-8 h-9" placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
    </div>
    <span className="text-xs text-muted-foreground">{count} résultat(s)</span>
    <Button data-testid={`${testid}-export`} size="sm" variant="outline" className="rounded-sm gap-1 ml-auto" onClick={onExport}>
      <Download className="h-3.5 w-3.5" /> Exporter
    </Button>
  </div>
);

export const FilterChips = ({ options, value, onChange, testid }) => (
  <div className="mb-3 flex flex-wrap gap-1.5">
    {options.map(([v, label, count]) => (
      <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
        className={`px-3 py-1 text-xs font-semibold border transition-colors ${value === v ? "bg-primary text-white border-primary" : "border-border hover:border-accent"}`}>
        {label}{count != null && ` (${count})`}
      </button>
    ))}
  </div>
);

const usePager = (rows, size = 20) => {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const cur = Math.min(page, pages - 1);
  return {
    rows: rows.slice(cur * size, (cur + 1) * size),
    pager: pages > 1 && (
      <div className="mt-3 flex items-center gap-2 text-sm">
        <Button size="sm" variant="outline" className="rounded-sm" disabled={cur === 0} onClick={() => setPage(cur - 1)}>Précédent</Button>
        <span className="text-xs text-muted-foreground">Page {cur + 1} / {pages}</span>
        <Button size="sm" variant="outline" className="rounded-sm" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}>Suivant</Button>
      </div>
    ),
  };
};

const match = (q, ...vals) => !q || vals.some((v) => String(v || "").toLowerCase().includes(q.toLowerCase()));
const money = (v, c = "EUR") => fmtMoney(v, c);

// MVP mode opérateur : le client traite avec Colis2Go — le partenaire reste interne/dormant
const opLabel = (s, pMap) => {
  if (s.module === "shopship") return "Entrepôt Colis2Go";
  const name = pMap[s.partner_id]?.company_name;
  if (!name || name === "Colis2Go Operations") return "Colis2Go";
  return `Colis2Go (interne : ${name})`;
};

export const DisputesPanel = ({ disputes, filter, setFilter, onResolve }) => {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const FILTERS = [
    ["open", "Ouverts", disputes.filter((d) => d.status === "open").length],
    ["resolved", "Résolus", disputes.filter((d) => d.status === "resolved").length],
    ["all", "Tous", disputes.length],
  ];
  const filtered = disputes.filter((d) => (filter === "all" || d.status === (filter || "open"))
    && match(search, d.reference, d.customer_name, d.partner_name, d.type));
  return (
    <div>
      <FilterChips options={FILTERS} value={filter || "open"} onChange={setFilter} testid="dispfilter" />
      <ListToolbar search={search} setSearch={setSearch} count={filtered.length} testid="disputes"
        onExport={() => exportCsv(filtered.map((d) => ({ numero: d.id, expedition: d.reference, client: d.customer_name, partenaire: d.partner_name, motif: d.type, date: (d.created_at || "").slice(0, 10), statut: d.status })), "litiges")} />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>N° litige</TableHead><TableHead>Expédition</TableHead><TableHead>Client</TableHead><TableHead>Partenaire</TableHead><TableHead>Motif</TableHead><TableHead>Ouvert le</TableHead><TableHead>Statut</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id} data-testid={`dispute-row-${d.id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => setSel(d)}>
                <TableCell className="font-mono text-xs">{d.id.slice(0, 12)}</TableCell>
                <TableCell className="font-mono font-semibold">{d.reference}</TableCell>
                <TableCell>{d.customer_name}</TableCell>
                <TableCell>{d.partner_name}</TableCell>
                <TableCell><Badge className="rounded-sm bg-secondary text-primary">{d.type}</Badge></TableCell>
                <TableCell className="text-xs">{(d.created_at || "").slice(0, 10)}</TableCell>
                <TableCell><Badge className={`rounded-sm ${d.status === "open" ? "bg-red-100 text-red-700" : "bg-emerald-600 text-white"}`}>{d.status === "open" ? "Ouvert" : "Résolu"}</Badge></TableCell>
                <TableCell><Button size="sm" variant="outline" className="rounded-sm text-xs" data-testid={`dispute-examine-${d.id}`} onClick={(e) => { e.stopPropagation(); setSel(d); }}>Examiner</Button></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-muted-foreground">Aucun litige pour ce filtre.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent>
          {sel && (
            <>
              <DialogHeader><DialogTitle>Litige — {sel.reference}</DialogTitle>
                <DialogDescription>{sel.type} · ouvert le {(sel.created_at || "").slice(0, 10)}</DialogDescription></DialogHeader>
              <div className="space-y-2 text-sm">
                <div><strong>Client :</strong> {sel.customer_name} · <strong>Partenaire :</strong> {sel.partner_name}</div>
                <p className="border border-border p-3 bg-muted/30">{sel.description || "—"}</p>
                {sel.resolution && <p className="text-emerald-700"><strong>Résolution :</strong> {sel.resolution}</p>}
              </div>
              {sel.status === "open" && onResolve && (
                <Button data-testid="dispute-resolve" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={async () => { await onResolve(sel); setSel(null); }}>Marquer comme résolu</Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GmvBlock = ({ title, data, testid, cur = "EUR" }) => (
  <div className="border border-border bg-card p-4" data-testid={testid}>
    <h4 className="font-heading font-bold text-sm">{title}</h4>
    <div className="mt-2 space-y-1.5">
      {data.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-sm">
          <span className="truncate mr-2">{k}</span><span className="font-semibold whitespace-nowrap">{money(Math.round(v * 100) / 100, cur)}</span>
        </div>
      ))}
      {data.length === 0 && <p className="text-xs text-muted-foreground">Aucune donnée sur la période.</p>}
    </div>
  </div>
);

// ---------- ANALYTIQUE GMV ----------
export const ClientsPanel = ({ users, shipments, purchases, isSuper = false, onChanged }) => {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [comm, setComm] = useState(null);
  const setActive = async (u, active) => {
    try {
      await api.post(`/admin/users/${u.user_id}/status`, { active });
      toast.success(active ? "Compte réactivé" : "Compte suspendu");
      setSel({ ...u, active });
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeUser = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.user_id}`);
      toast.success(data.action === "deleted" ? "Compte supprimé définitivement" : "Compte archivé");
      setSel(null); onChanged && onChanged();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm("Ce compte possède des colis/expéditions — la suppression casserait l'historique.\nARCHIVER LE COMPTE (références conservées, connexion bloquée) ?")) {
          await api.delete(`/admin/users/${u.user_id}?archive=true`);
          toast.success("Compte archivé — historique conservé");
          setSel(null); onChanged && onChanged();
        }
      } else toast.error(e.response?.data?.detail || "Erreur");
    }
  };
  const shipCount = useMemo(() => { const m = {}; shipments.forEach((s) => { m[s.customer_id] = (m[s.customer_id] || 0) + 1; }); return m; }, [shipments]);
  const purCount = useMemo(() => { const m = {}; (purchases || []).forEach((p) => { m[p.customer_id] = (m[p.customer_id] || 0) + 1; }); return m; }, [purchases]);
  const filtered = users.filter((u) => match(search, u.name, u.email, u.country, u.phone));
  const { rows, pager } = usePager(filtered);
  return (
    <div>
      <ListToolbar search={search} setSearch={setSearch} count={filtered.length} testid="clients"
        onExport={() => exportCsv(filtered.map((u) => ({ nom: u.name, email: u.email, pays: u.country, email_verifie: u.email_verified ? "Oui" : "Non", telephone: u.phone, expeditions: shipCount[u.user_id] || 0, achats_shopship: purCount[u.user_id] || 0, inscription: (u.created_at || "").slice(0, 10), statut: u.active === false ? "Suspendu" : "Actif", role: u.role })), "clients")} />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Email vérifié</TableHead><TableHead>Téléphone</TableHead><TableHead>Pays</TableHead><TableHead>Rôle</TableHead><TableHead>Expéditions</TableHead><TableHead>Achats S&amp;S</TableHead><TableHead>Inscription</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.user_id} data-testid={`client-row-${u.user_id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => setSel(u)}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell>{u.email_verified ? <Badge className="rounded-sm bg-emerald-600 text-white">Oui</Badge> : <Badge className="rounded-sm bg-amber-100 text-amber-700">Non</Badge>}</TableCell>
                <TableCell className="text-xs">{u.phone || "—"}</TableCell>
                <TableCell>{u.country || "—"}</TableCell>
                <TableCell><Badge className="rounded-sm">{u.role}</Badge></TableCell>
                <TableCell>{shipCount[u.user_id] || 0}</TableCell>
                <TableCell>{purCount[u.user_id] || 0}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</TableCell>
                <TableCell>{u.archived ? <Badge className="rounded-sm bg-muted text-muted-foreground">Archivé</Badge> : u.active === false ? <Badge className="rounded-sm bg-red-100 text-red-700">Suspendu</Badge> : <Badge className="rounded-sm bg-secondary text-primary">Actif</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {pager}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent>
          {sel && (
            <>
              <DialogHeader><DialogTitle>{sel.name}</DialogTitle><DialogDescription>{sel.email}</DialogDescription></DialogHeader>
              <div className="space-y-1.5 text-sm" data-testid="client-detail">
                <div><strong>Email vérifié :</strong> {sel.email_verified ? "Oui" : "Non"} · <strong>Téléphone :</strong> {sel.phone || "Non renseigné"} {sel.phone && `(${sel.phone_verified ? "vérifié" : "non vérifié"})`}</div>
                <div><strong>Pays :</strong> {sel.country || "—"} · <strong>Ville :</strong> {sel.city || "—"}</div>
                <div><strong>Code client :</strong> {sel.client_code || "—"}</div>
                <div><strong>Expéditions :</strong> {shipCount[sel.user_id] || 0} · <strong>Achats en ligne :</strong> {purCount[sel.user_id] || 0}</div>
                <div><strong>Inscrit le :</strong> {(sel.created_at || "").slice(0, 10)} · <strong>Dernière connexion :</strong> {(sel.last_login || "").slice(0, 16).replace("T", " ") || "—"}</div>
              </div>
              {sel.role === "customer" && (
                <div className="mt-2 flex flex-wrap gap-2 border-t pt-3">
                  <Button size="sm" data-testid="client-comm" className="rounded-sm bg-accent text-white gap-1.5"
                    onClick={() => { setComm({ customer_id: sel.user_id, customer_name: sel.name, reference: sel.client_code || "" }); }}>
                    <Send className="h-3.5 w-3.5" /> Envoyer une communication
                  </Button>
                  {sel.active === false ? (
                    <Button size="sm" data-testid="client-reactivate" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setActive(sel, true)}>Réactiver</Button>
                  ) : (
                    <Button size="sm" variant="outline" data-testid="client-suspend" className="rounded-sm text-red-600 border-red-200" onClick={() => setActive(sel, false)}>Suspendre / Désactiver</Button>
                  )}
                  {isSuper && (
                    <Button size="sm" variant="outline" data-testid="client-delete" className="rounded-sm text-red-700 border-red-300" onClick={() => removeUser(sel)}>Supprimer / Archiver</Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      {comm && <CommDialog target={comm} onClose={() => setComm(null)} />}
    </div>
  );
};

// ---------- ROUTES ----------
export const RoutesPanel = ({ routes, filter, setFilter, onToggle }) => {
  const [search, setSearch] = useState("");
  const FILTERS = [
    ["all", "Toutes", routes.length],
    ["active", "Actives", routes.filter((r) => r.active).length],
    ["air", "Aériennes", routes.filter((r) => r.mode === "air" && r.active).length],
    ["sea", "Maritimes", routes.filter((r) => r.mode === "sea" && r.active).length],
  ];
  const filtered = routes.filter((r) => {
    if (filter === "active" && !r.active) return false;
    if (filter === "air" && (r.mode !== "air" || !r.active)) return false;
    if (filter === "sea" && (r.mode !== "sea" || !r.active)) return false;
    return match(search, r.origin_city, r.origin_country, r.dest_city, r.dest_country, r.partner_name);
  });
  const price = (r) => {
    const tiers = r.mode === "air" ? r.weight_tiers : r.cbm_tiers;
    const p = (tiers || [])[0]?.price;
    return p != null ? `${p} ${r.currency || "EUR"}/${r.mode === "air" ? "kg" : "CBM"}` : "—";
  };
  return (
    <div>
      <FilterChips options={FILTERS} value={filter || "all"} onChange={setFilter} testid="routefilter" />
      <ListToolbar search={search} setSearch={setSearch} count={filtered.length} testid="routes"
        onExport={() => exportCsv(filtered.map((r) => ({ origine: `${r.origin_city}, ${r.origin_country}`, destination: `${r.dest_city}, ${r.dest_country}`, partenaire: r.partner_name, mode: r.mode, tarif: price(r), frequence: r.frequency || "", statut: r.active ? "Active" : "Inactive" })), "routes")} />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Origine</TableHead><TableHead>Destination</TableHead><TableHead>Partenaire</TableHead><TableHead>Mode</TableHead><TableHead>Tarif</TableHead><TableHead>Fréquence</TableHead><TableHead>Délai</TableHead><TableHead>Active</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`admin-route-${r.id}`}>
                <TableCell className="font-semibold">{r.origin_city}, {r.origin_country}</TableCell>
                <TableCell className="font-semibold">{r.dest_city}, {r.dest_country}</TableCell>
                <TableCell>{r.partner_name}</TableCell>
                <TableCell>{r.mode === "air" ? <Plane className="h-4 w-4 text-accent" /> : <Ship className="h-4 w-4 text-accent" />}</TableCell>
                <TableCell>{price(r)}</TableCell>
                <TableCell className="text-xs">{r.frequency || "—"}</TableCell>
                <TableCell className="text-xs">{r.delay_days || r.transit_days || "—"}</TableCell>
                <TableCell><Switch data-testid={`route-active-${r.id}`} checked={!!r.active} onCheckedChange={(v) => onToggle(r, v)} /></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-muted-foreground">Aucune route pour ce filtre.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ---------- PAYS & VILLES (drill-down) ----------
export const GeoPanel = ({ geo, routes, partners, filter, setFilter, onUpdateCountry, onUpdateCity, onChange }) => {
  const [search, setSearch] = useState("");
  const [editCountry, setEditCountry] = useState(null);
  const [cityMgr, setCityMgr] = useState(null);
  const countries = geo?.countries || [];
  const cities = geo?.cities || [];
  const routeCountByCountry = (name) => routes.filter((r) => r.active && (r.origin_country === name || r.dest_country === name)).length;
  const routeCountByCity = (name) => routes.filter((r) => r.active && (r.origin_city === name || r.dest_city === name)).length;
  const partnersByCity = (name) => partners.filter((p) => p.city === name).length;
  const view = filter?.startsWith("cities") ? "cities" : "countries";
  const activeOnly = (filter || "").endsWith("-active");
  const list = view === "countries"
    ? countries.filter((c) => (!activeOnly || c.active) && match(search, c.name, c.code))
    : cities.filter((c) => (!activeOnly || c.active) && match(search, c.name, c.country));
  return (
    <div>
      <FilterChips testid="geofilter" value={filter || "countries"} onChange={setFilter}
        options={[["countries", "Tous les pays", countries.length], ["countries-active", "Pays actifs", countries.filter((c) => c.active).length],
                  ["cities", "Toutes les villes", cities.length], ["cities-active", "Villes actives", cities.filter((c) => c.active).length]]} />
      <ListToolbar search={search} setSearch={setSearch} count={list.length} testid="geo"
        onExport={() => exportCsv(view === "countries"
          ? list.map((c) => ({ pays: c.name, origine: c.origin_enabled ? "Oui" : "Non", destination: c.dest_enabled ? "Oui" : "Non", shop_ship: c.shopship_enabled ? "Oui" : "Non", marketplace: c.marketplace_enabled !== false ? "Oui" : "Non", villes: cities.filter((x) => x.country === c.name).length, routes: routeCountByCountry(c.name), statut: c.active ? "Actif" : "Inactif" }))
          : list.map((c) => ({ ville: c.name, pays: c.country, origine: c.origin_enabled ? "Oui" : "Non", destination: c.dest_enabled ? "Oui" : "Non", partenaires: partnersByCity(c.name), routes: routeCountByCity(c.name), statut: c.active ? "Active" : "Inactive" })), view === "countries" ? "pays" : "villes")} />
      <div className="overflow-x-auto">
        {view === "countries" ? (
          <Table>
            <TableHeader><TableRow><TableHead>Pays</TableHead><TableHead>Origine</TableHead><TableHead>Destination</TableHead><TableHead>Achats en ligne</TableHead><TableHead>Ville principale</TableHead><TableHead>Villes</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} data-testid={`country-row-${c.code}`}>
                  <TableCell className="font-semibold">{c.flag} {c.name}</TableCell>
                  <TableCell><Switch checked={!!c.origin_enabled} onCheckedChange={(v) => onUpdateCountry(c, { origin_enabled: v })} /></TableCell>
                  <TableCell><Switch checked={!!c.dest_enabled} onCheckedChange={(v) => onUpdateCountry(c, { dest_enabled: v })} /></TableCell>
                  <TableCell>{c.shopship_enabled ? <Badge className="rounded-sm bg-emerald-600 text-white">Oui</Badge> : <Badge className="rounded-sm bg-muted text-muted-foreground">Non</Badge>}</TableCell>
                  <TableCell className="text-sm">{c.main_city || "—"}</TableCell>
                  <TableCell>{cities.filter((x) => x.country_code === c.code).length}</TableCell>
                  <TableCell><Switch data-testid={`country-active-${c.code}`} checked={!!c.active} onCheckedChange={(v) => onUpdateCountry(c, { active: v })} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1" data-testid={`edit-country-${c.code}`}
                        onClick={() => setEditCountry(c)}><Pencil className="h-3.5 w-3.5" /> Modifier</Button>
                      {c.multi_city && (
                        <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1" data-testid={`manage-cities-${c.code}`}
                          onClick={() => setCityMgr(c)}><Building2 className="h-3.5 w-3.5" /> Gérer les villes</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Ville</TableHead><TableHead>Pays</TableHead><TableHead>Origine</TableHead><TableHead>Destination</TableHead><TableHead>Marketplace</TableHead><TableHead>Partenaires</TableHead><TableHead>Routes</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} data-testid={`city-row-${c.id}`}>
                  <TableCell className="font-semibold">{c.name}</TableCell>
                  <TableCell>{c.country}</TableCell>
                  <TableCell><Switch checked={!!c.origin_enabled} onCheckedChange={(v) => onUpdateCity(c, { origin_enabled: v })} /></TableCell>
                  <TableCell><Switch checked={!!c.dest_enabled} onCheckedChange={(v) => onUpdateCity(c, { dest_enabled: v })} /></TableCell>
                  <TableCell><Switch data-testid={`city-marketplace-${c.id}`} checked={c.marketplace_active !== false} onCheckedChange={(v) => onUpdateCity(c, { marketplace_active: v })} /></TableCell>
                  <TableCell>{partnersByCity(c.name)}</TableCell>
                  <TableCell>{routeCountByCity(c.name)}</TableCell>
                  <TableCell><Switch data-testid={`city-active-${c.id}`} checked={!!c.active} onCheckedChange={(v) => onUpdateCity(c, { active: v })} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <CountryEditDialog country={editCountry} onClose={() => setEditCountry(null)} onSave={onUpdateCountry} />
      <CitiesDialog country={cityMgr} cities={cities} onClose={() => setCityMgr(null)} onUpdateCity={onUpdateCity} onChange={onChange} />
    </div>
  );
};

/* Fiche pays — MODIFIER (Phase 3B) */
const CountryEditDialog = ({ country, onClose, onSave }) => {
  const [f, setF] = useState(null);
  React.useEffect(() => { if (country) setF({ ...country }); }, [country]);
  if (!country || !f) return null;
  const Row = ({ label, k, testid }) => (
    <label className="flex items-center justify-between gap-3 border border-border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch data-testid={testid} checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} />
    </label>
  );
  const save = async () => {
    await onSave(country, {
      active: f.active, origin_enabled: f.origin_enabled, shopship_enabled: f.shopship_enabled,
      goods_enabled: f.goods_enabled, dest_enabled: f.dest_enabled, multi_city: f.multi_city,
      main_city: f.main_city || "",
    });
    toast.success("Pays mis à jour");
    onClose();
  };
  return (
    <Dialog open={!!country} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="country-edit-dialog">
        <DialogHeader>
          <DialogTitle>{country.flag} {country.name}</DialogTitle>
          <DialogDescription>Réglages du pays</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Row label="Actif" k="active" testid="cty-active" />
          <Row label="Origine" k="origin_enabled" testid="cty-origin" />
          <Row label="Achats en ligne" k="shopship_enabled" testid="cty-online" />
          <Row label="J'ai déjà ma marchandise" k="goods_enabled" testid="cty-goods" />
          <Row label="Destination" k="dest_enabled" testid="cty-dest" />
          <Row label="Gestion de plusieurs villes" k="multi_city" testid="cty-multicity" />
          <div>
            <Label>Ville principale d'arrivée</Label>
            <Input data-testid="cty-main-city" className="rounded-sm" value={f.main_city || ""}
              placeholder="Ex : Abidjan" onChange={(e) => setF({ ...f, main_city: e.target.value })} />
          </div>
        </div>
        <Button className="mt-2 w-full rounded-sm bg-accent text-white" data-testid="cty-save" onClick={save}>Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
};

/* GÉRER LES VILLES — visible uniquement si multi_city (Phase 3B) */
const CitiesDialog = ({ country, cities, onClose, onUpdateCity, onChange }) => {
  const [newCity, setNewCity] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  if (!country) return null;
  const list = cities.filter((x) => x.country_code === country.code);
  const addCity = async () => {
    const name = newCity.trim();
    if (!name) return;
    if (list.some((x) => x.name.toLowerCase() === name.toLowerCase())) { toast.error("Cette ville existe déjà"); return; }
    await api.post("/admin/cities", { country_code: country.code, name });
    toast.success("Ville ajoutée");
    setNewCity(""); onChange && onChange();
  };
  const rename = async (c) => {
    const name = editName.trim();
    if (!name || name === c.name) { setEditId(null); return; }
    await api.put(`/admin/cities/${c.id}`, { name });
    toast.success("Ville modifiée");
    setEditId(null); onChange && onChange();
  };
  return (
    <Dialog open={!!country} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cities-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{country.flag} Villes — {country.name}</DialogTitle>
          <DialogDescription>Ville principale : {country.main_city || "—"} · {list.length} ville(s)</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="new-city-name" className="rounded-sm" placeholder="Ajouter une ville…"
            value={newCity} onChange={(e) => setNewCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCity()} />
          <Button className="rounded-sm bg-accent text-white gap-1" data-testid="add-city-confirm" onClick={addCity}><Plus className="h-4 w-4" /> Ajouter</Button>
        </div>
        <div className="space-y-1.5">
          {list.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 border border-border px-3 py-2" data-testid={`mgr-city-${c.id}`}>
              {editId === c.id ? (
                <div className="flex flex-1 gap-2">
                  <Input data-testid="edit-city-name" className="rounded-sm h-8" value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && rename(c)} />
                  <Button size="sm" className="rounded-sm h-8 bg-accent text-white" data-testid="edit-city-save" onClick={() => rename(c)}><Check className="h-4 w-4" /></Button>
                </div>
              ) : (
                <span className={`text-sm font-medium ${!c.active ? "text-muted-foreground line-through" : ""}`}>
                  {c.name}{country.main_city === c.name && <Badge className="ml-2 rounded-sm bg-accent text-white">Principale</Badge>}
                </span>
              )}
              <div className="flex items-center gap-2">
                {editId !== c.id && (
                  <Button size="sm" variant="ghost" className="h-8 px-2" data-testid={`edit-city-${c.id}`}
                    onClick={() => { setEditId(c.id); setEditName(c.name); }}><Pencil className="h-3.5 w-3.5" /></Button>
                )}
                <Switch data-testid={`mgr-city-active-${c.id}`} checked={!!c.active} onCheckedChange={(v) => onUpdateCity(c, { active: v })} />
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-sm text-muted-foreground">Aucune ville enregistrée pour ce pays.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ChevronHint = () => (
  <span className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-accent font-semibold">Voir les détails <ChevronRight className="h-3 w-3" /></span>
);
